import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer, provideEnvironmentInitializer } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { FullRouterStateSerializer, routerReducer, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { environment } from '@environments/environment';

import { effects, RouterCleanSerializer } from '@app/store';

// Swagger service (API fake-nomenclature)
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PageNotFoundComponent } from '@app/core/404/page-not-found.component';
import { PageComponent } from '@app/core/layout/page.component';

import { DialogService } from '@app/shared/dialog/dialog.service';
import { EspaceIndicatorComponent } from '@app/shared/espace-indicator/espace-indicator.component';

import {
    AuthenticationFormComponent,
    authLogoutMetaReducer,
    AuthModule,
    AuthSilentComponent,
    OidcConfig,
    OidcConfigParameters
} from '@pcscol-portail/ng-auth';
import { AuthzConfig, AuthzConfigParameters, AuthzModule } from '@pcscol-portail/ng-authz';
import {
    Config,
    ConfigModule,
    ConfigService,
    FooterComponent,
    HeaderComponent,
    MenuComponent,
    MenuEntryComponent,
    MenuEntryWithChildrenComponent,
    PcscolIconsModule
} from '@pcscol-portail/composants-pegase';
import { Configuration as AuthzApiConfig, ApiModule as AuthzApiModule } from '@pcscol/authz-autorisation-api-v5-angular';
import { Configuration as CocApiConfig } from '@pcscol/coc-application-api-v2-angular';
import { Configuration as OdfApiConfig, ApiModule as OdfApiModule } from '@pcscol/odf-api-v1-angular';
import { Configuration as NomenclatureApiConfig, ApiModule as NomenclatureApiModule } from '@pcscol/ref-api-v1-angular';
import { PageAccueilComponent } from './accueil/accueil.component';
import { GlobalErrorHandler } from './error/global-error-handler';
import { AppInitializerService } from './initializer/app-initializer.service';
import { LoaderInterceptor } from './loader/loader.interceptor';
import { LoaderModule } from './loader/loader.module';
import { NotificationModule } from './notifications/notification.module';

export function authzConfig(configService: ConfigService) {
    return new AuthzConfig(configService.getConfig().authz as AuthzConfigParameters);
}

export function authConfig(configService: ConfigService) {
    return new OidcConfig(configService.getConfig().auth as OidcConfigParameters);
}

export function nomenclatureApiConfig(configService: ConfigService) {
    return new NomenclatureApiConfig({
        basePath: configService.getConfig().ref.url + 'api/v1/ref'
    });
}

export function odfApiConfig(configService: ConfigService) {
    return new OdfApiConfig({
        basePath: configService.getConfig().odf.url + 'api/odf/v1'
    });
}

export function authzApiConfig(configService: ConfigService) {
    return new AuthzApiConfig({
        basePath: configService.getConfig().authz.url + 'api/authz/autorisation/v5'
    });
}

export function cocApiConfig(configService: ConfigService) {
    return new CocApiConfig({
        basePath: configService.getConfig().coc.url + 'api/coc/application/v2'
    });
}

const metaReducers = [authLogoutMetaReducer];

export function initializeApp(appInitService: AppInitializerService) {
    return (): Promise<void> => {
        return appInitService.initialize();
    };
}

export function initializeDialogService() {
    return () => {
        inject(DialogService);
    };
}

@NgModule({
    exports: [
        // app
        PageNotFoundComponent,
        PageComponent,
        PageAccueilComponent
    ],
    imports: [
        // angular
        CommonModule,
        NomenclatureApiModule,
        AuthzApiModule,
        OdfApiModule,
        RouterModule,
        A11yModule,
        MatProgressSpinnerModule,
        // ngrx
        StoreModule.forRoot(
            { router: routerReducer },
            {
                metaReducers,
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false
                }
            }
        ),
        EffectsModule.forRoot(effects),
        StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer, stateKey: 'router' }),
        environment.production ? [] : StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        // material
        MatToolbarModule,
        MatIconModule,
        MatSidenavModule,
        MatListModule,
        MatCardModule,
        MatButtonModule,
        MatSnackBarModule,
        MatTooltipModule,
        // pcscol
        AuthModule,
        ConfigModule.forRoot(new Config({ location: 'assets/config/config.json' })),
        HeaderComponent,
        FooterComponent,
        MenuComponent,
        MenuEntryComponent,
        MenuEntryWithChildrenComponent,
        AuthenticationFormComponent,
        AuthSilentComponent,
        // app
        LoaderModule,
        NotificationModule,
        PcscolIconsModule,
        AuthModule.forRoot(),
        AuthzModule.forRoot(),
        EspaceIndicatorComponent,
        PageComponent,
        PageNotFoundComponent,
        PageAccueilComponent
    ],
    providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: RouterStateSerializer, useClass: RouterCleanSerializer },
        AppInitializerService,
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        provideAppInitializer(() => {
            const initializerFn = initializeApp(inject(AppInitializerService));
            return initializerFn();
        }),
        provideEnvironmentInitializer(() => {
            const initializerFn = initializeDialogService();
            return initializerFn();
        }),
        {
            provide: OidcConfig,
            useFactory: authConfig,
            deps: [ConfigService]
        },
        {
            provide: AuthzConfig,
            useFactory: authzConfig,
            deps: [ConfigService]
        },
        {
            provide: NomenclatureApiConfig,
            useFactory: nomenclatureApiConfig,
            deps: [ConfigService]
        },
        {
            provide: AuthzApiConfig,
            useFactory: authzApiConfig,
            deps: [ConfigService]
        },
        {
            provide: OdfApiConfig,
            useFactory: odfApiConfig,
            deps: [ConfigService]
        },
        {
            provide: CocApiConfig,
            useFactory: cocApiConfig,
            deps: [ConfigService]
        },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class CoreModule {
    constructor() {
        const parentModule = inject(CoreModule, { optional: true, skipSelf: true })!;

        // on vérifie que CoreModule n'est pas importé par un module shared/features
        if (parentModule) {
            throw new Error('CoreModule is already loaded. Import only in AppModule');
        }
    }
}
